/**
 * Models for the Assignments tab (Tab 2) of the Station Assignment workflow.
 *
 * These types mirror the backend facade response (RnRRCCFacadeService).
 */

// ─── Enums ──────────────────────────────────────────────────────────────────────

/**
 * Staffing mode for a station assignment process path.
 * Determines how associates are rendered:
 *   - PROCESS_LINES: workstation cards (lines with slots)
 *   - DEPARTMENT: associate pool read from workstations[0].assignedAssociates
 */
export const StaffingMode = {
  PROCESS_LINES: 'PROCESS_LINES',
  DEPARTMENT: 'DEPARTMENT',
} as const;
export type StaffingMode = typeof StaffingMode[keyof typeof StaffingMode];

/**
 * Path type indicates whether the function is direct or indirect.
 * Used for badge display and metrics row selection.
 */
export const PathType = {
  DIRECT: 'DIRECT',
  INDIRECT: 'INDIRECT',
} as const;
export type PathType = typeof PathType[keyof typeof PathType];

/**
 * Who made an assignment decision. `ALGORITHM` decisions are produced by the
 * planning service; `USER_OVERRIDE` decisions are produced in this UI when a
 * planner manually assigns or unassigns an associate.
 *
 * Mirrors `DecisionSource` in RnRRCCShiftPlanningCommons (see CR-298571676).
 */
export const DecisionSource = {
  ALGORITHM: 'ALGORITHM',
  USER_OVERRIDE: 'USER_OVERRIDE',
} as const;
export type DecisionSource = typeof DecisionSource[keyof typeof DecisionSource];

/**
 * The reason codes this UI *authors* — the USER_OVERRIDE reasons a planner can
 * pick from the reason dropdown when assigning or unassigning an associate.
 *
 * ALGORITHM reason codes are intentionally NOT enumerated here. They are owned
 * by the planning service: the frontend never authors them, it only round-trips
 * whatever the backend sends back on publish. Hardcoding them would tightly
 * couple this UI to the backend's code set (every new/renamed algorithm code
 * would need a frontend change, and an unknown code would break the contract).
 * See {@link DecisionReasonCodeValue} for the round-trip type.
 */
export const DecisionReasonCode = {
  OPERATIONAL_NEED: 'OPERATIONAL_NEED',
  ASSOCIATE_REQUEST: 'ASSOCIATE_REQUEST',
  TRAINING_PERMISSION: 'TRAINING_PERMISSION',
  ERGONOMIC_CONCERN: 'ERGONOMIC_CONCERN',
  LABOR_SHARE: 'LABOR_SHARE',
  OTHER: 'OTHER',
} as const;
export type DecisionReasonCode = typeof DecisionReasonCode[keyof typeof DecisionReasonCode];

/**
 * A reason code as carried on a decision. It is either one of the frontend's
 * own USER_OVERRIDE codes ({@link DecisionReasonCode}) or an opaque
 * backend-authored ALGORITHM code, passed through as a plain string so it
 * round-trips to the facade untouched without coupling the UI to the backend's
 * evolving algorithm code set.
 */
export type DecisionReasonCodeValue = DecisionReasonCode | string;

/**
 * Metadata recording why an associate is in their current slot (or why they
 * were returned to the unassigned pool). Attached to an
 * {@link AssignmentAssociate}. Algorithm placements arrive from the facade
 * (their `reasonCode` is an opaque backend string that this UI round-trips);
 * planner assign/unassign actions stamp a `USER_OVERRIDE` decision from the
 * reason dropdown (`reasonCode` is a {@link DecisionReasonCode}) plus optional
 * free-text context (`decisionReason`).
 */
export interface AssignmentDecision {
  source?: DecisionSource;
  reasonCode?: DecisionReasonCodeValue;
  /** Free-text context; UI-controlled. Optional for all reason codes. */
  decisionReason?: string;
}

/**
 * The reason options shown in the required reason dropdown when a planner
 * assigns or unassigns an associate. Order matches the design mock.
 */
export const USER_OVERRIDE_REASON_OPTIONS: ReadonlyArray<{
  value: DecisionReasonCode;
  label: string;
}> = [
  { value: DecisionReasonCode.OPERATIONAL_NEED, label: 'Operational Need' },
  { value: DecisionReasonCode.ASSOCIATE_REQUEST, label: 'Associate Request' },
  { value: DecisionReasonCode.TRAINING_PERMISSION, label: 'Training/Permission' },
  { value: DecisionReasonCode.ERGONOMIC_CONCERN, label: 'Ergonomic Concern' },
  { value: DecisionReasonCode.LABOR_SHARE, label: 'Labor Share' },
  { value: DecisionReasonCode.OTHER, label: 'Other' },
];

/**
 * The decision captured from a reason dropdown and optional free-text context
 * field in the assign/unassign modals. Passed to the parent so it can stamp a
 * `USER_OVERRIDE` {@link AssignmentDecision} onto the affected associate.
 */
export interface OverrideDecision {
  reasonCode: DecisionReasonCode;
  decisionReason?: string;
}

// ─── Interfaces ─────────────────────────────────────────────────────────────────

/**
 * A single associate in the assignments view.
 *
 * `assignedWorkstationId` is a convenience marker kept in sync by the
 * assignment helpers. The authoritative location for an associate is the
 * process-path / workstation structure itself (see `GetStationAssignmentsResponse`).
 */
export interface AssignmentAssociate {
  /**
   * The associate's id. Absent (`undefined`) for an empty
   * (planned-but-unfilled) slot: when a workstation has more planned positions
   * than assigned associates, the API returns placeholder entries that omit
   * `associateId`/`associateName` (other fields keep defaults, and workstation
   * slots still carry `assignedWorkstationId`). A real associate always has an
   * id — even one with a blank `associateName` — so `associateId` is the
   * canonical empty-slot marker; use {@link isEmptySlot}.
   */
  associateId?: string;
  associateName: string;
  /**
   * The associate's login alias, sent by the getStationAssignment facade
   * alongside `associateName` and `associateId`. Used to construct the
   * badge photo URL (see `getBadgePhotoUrl`).
   */
  associateAlias?: string;
  /**
   * The associate's badge barcode id, sent by the getStationAssignment facade.
   * The UI does not render or author it; it is round-tripped untouched so it is
   * preserved on publish (see the round-trip note on {@link AssignmentDecision}).
   */
  badgeBarcodeId?: string;
  /**
   * The ergonomic orientation of the workstation slot this entry occupies
   * (e.g. `"RIGHT"` / `"LEFT"`). It is a per-slot property (each slot maps to
   * one entry here), sent by the getStationAssignment facade. The UI does not
   * render or author it; it is round-tripped untouched so it is preserved on
   * publish (see the round-trip note on {@link AssignmentDecision}).
   */
  ergonomicProperty?: string;
  /**
   * Front/back zone of the workstation slot this entry occupies within its
   * line (`"FRONT"`, `"MIDDLE"` or `"BACK"`), sent by the getStationAssignment
   * facade on assigned and empty slots. `null` for plans produced before it was
   * populated. Like `ergonomicProperty` it is a slot-level property: the UI does
   * not render or author it, and it stays with the slot (not the associate) when
   * an associate is moved, so it is preserved on publish. Typed as an opaque
   * string so a new backend value round-trips untouched.
   */
  stationPosition?: string | null;
  /**
   * 0-based physical position of the workstation slot this entry occupies
   * within its line (or within its process path in DEPARTMENT mode), from the
   * site layout. `null` for plans produced before it was populated. Slot-level
   * and round-tripped untouched, exactly like `stationPosition`.
   */
  workstationSortOrder?: number | null;
  avatarUrl?: string;
  isClocked: boolean;
  hasWarning: boolean;
  trainedPaths: string[];
  assignedWorkstationId?: string;
  /**
   * Why this associate is in their current slot, or why they were returned to
   * the unassigned pool. Algorithm placements carry a decision from the facade;
   * a planner assign/unassign stamps a `USER_OVERRIDE` decision (reason code +
   * optional free text). Optional and additive: absent on associates that have
   * never been placed or overridden.
   */
  decision?: AssignmentDecision;
  /**
   * True when this associate originated from the PA/Lead/HR pool
   * (`unassignedPALeadHRAssociates`) — a backend `jobLevel >= 2` associate the
   * algorithm excluded from workstation assignment (decision reason
   * `PA_LEAD_HR_EXCLUDED`). While the associate sits in that pool this marker is
   * not required (pool membership is authoritative); it is stamped `true` when
   * the associate is assigned OUT of the pool so that on a later unassign they
   * return to the PA/Lead/HR box rather than the regular unassigned pool
   * (Model A identity). Optional and additive.
   */
  isPALeadHR?: boolean;
}

/**
 * Return `true` when the given entry is an empty (planned-but-unfilled) slot
 * rather than a real associate — i.e. it has no `associateId`. Empty slots
 * appear in `workstations[].assignedAssociates` (and, per the API contract,
 * possibly `nonWorkstationAssociates`) and render as dashed "add associate"
 * placeholders. Note a real associate may have a blank `associateName`, so the
 * name is not a reliable marker — only the id is.
 */
export function isEmptySlot(entry: AssignmentAssociate): boolean {
  return entry.associateId == null;
}

/**
 * A workstation (line) within a process path.
 */
export interface Workstation {
  workstationId: string;
  workstationName: string;
  assignedAssociates: AssignmentAssociate[];
  capacity: number;
  /**
   * 0-based physical position of this line within its process path, from the
   * site layout. `null` for DEPARTMENT-mode process paths and for plans
   * produced before it was populated. The UI does not render or author it; it
   * is round-tripped untouched so it is preserved on publish.
   */
  lineSortOrder?: number | null;
}

/**
 * Per-process-path metrics row. Fields marked optional apply only to certain
 * staffing modes:
 *   - `totalFlow`, `avgRatePerHour` are used for PROCESS_LINES.
 *   - `dr`, `avgScanRate`, `unitsPerHour`, `processedPackages` are used for
 *     DEPARTMENT.
 */
export interface ProcessPathMetrics {
  totalFlow?: number;
  actual: number;
  assigned: number;
  delta: number;
  avgRatePerHour?: number;
  dr?: number;
  avgScanRate?: number;
  unitsPerHour?: number;
  processedPackages?: number;
}

/**
 * A single process path (function) in the station assignment.
 *
 * `workstations` is always present:
 *   - PROCESS_LINES: multiple workstations (lines) with slots
 *   - DEPARTMENT: single workstation whose assignedAssociates are the pool
 */
export interface ProcessPath {
  fclmFunctionId: string;
  processPathName: string;
  pathType: PathType;
  staffingMode: StaffingMode;
  metrics: ProcessPathMetrics;
  workstations: Workstation[];
  /**
   * Associates assigned to this function but not to any specific workstation.
   * Populated by the facade (`nonWorkstationAssociates`) only for functions
   * that have no workstation slots at all (e.g. DEPARTMENT-mode paths).
   */
  nonWorkstationAssociates?: AssignmentAssociate[];
  departmentName?: string;
  processPathMappingName?: string;
  fclmProcessPathId?: number;
}

/**
 * Aggregate half-level metrics returned alongside each shift half.
 *
 * The real getStationAssignment facade returns `totalUnlocked`,
 * `totalClockedIn`, `totalActual` and an `assignedProcessPaths` array. The
 * per-category `assigned*` counts below are NOT sent by the API — they are
 * legacy fields consumed only by the pre-redesign `OldMetricsBar` /
 * `recomputeMetrics` path and are removed with the old screen. They are kept
 * optional so both the real response and the legacy path type-check. The new
 * screen derives its StatsRow counts from `processPaths` and does not read
 * this object.
 */
export interface AssignmentsMetrics {
  totalUnlocked: number;
  totalClockedIn: number;
  totalActual: number;
  /**
   * Per-process-path assignment breakdown from the real API. Its element
   * shape is not yet finalised in observed responses (the array has always
   * been empty), so it is typed permissively until a populated sample exists.
   */
  assignedProcessPaths?: Array<Record<string, unknown>>;
  assignedPrimary?: number;
  assignedProblemSolve?: number;
  assignedResale?: number;
  assignedCleanDecant?: number;
  assignedIndirect?: number;
}

/**
 * Request payload for the `/getStationAssignment` endpoint.
 */
export interface GetStationAssignmentsRequest {
  nodeId: string;
  warehouseId: string;
  ofdDate: string;
  clockWindowName: string;
  planId: string;
}

/**
 * Data for a single half of the shift.
 */
export interface StationAssignmentHalfData {
  associates: AssignmentAssociate[];
  /**
   * PA/Lead/HR associates (backend `jobLevel >= 2`) the algorithm excluded from
   * workstation assignment and surfaced as a separate pool, rendered as its own
   * "PA / Lead / HR" box below the Unassigned box. Optional/additive: absent or
   * empty when the plan has no such associates. Preserved through the publish
   * round-trip by the facade so an unassigned PA/Lead/HR associate returns to
   * this pool (Model A). Assigned members leave this list and live in the
   * process paths, carrying `isPALeadHR` so unassign can route them back.
   */
  unassignedPALeadHRAssociates?: AssignmentAssociate[];
  processPaths: ProcessPath[];
  metrics: AssignmentsMetrics;
}

/**
 * Return `true` when a shift half carries no plan data — no associates and no
 * process paths.
 *
 * The facade (RnRRCCFacadeService) sends an empty (non-null)
 * {@link StationAssignmentHalfData} — `associates: []`, `processPaths: []`,
 * zeroed metrics — for a half the backend did not produce. This happens for
 * the first half when a plan is generated/regenerated after the shift
 * midpoint ("second half only"): the backend sets `firstHalfStationAssignment`
 * to null and the facade converts that null into an empty object before it
 * reaches the UI. Recognising that empty half is how the UI decides to default
 * the selector to the populated half.
 *
 * Null-safe by design: a missing/`null` half object also counts as empty, and
 * the inner `associates`/`processPaths` reads are guarded, so callers never
 * assume either half (or its arrays) is present.
 */
export function isHalfEmpty(half?: StationAssignmentHalfData | null): boolean {
  return (
    half == null ||
    ((half.associates?.length ?? 0) === 0 && (half.processPaths?.length ?? 0) === 0)
  );
}

/**
 * Response from the `/getStationAssignment` endpoint.
 *
 * Contains assignment data for both halves of the shift. The UI selects
 * the active half based on the half selector without re-fetching.
 */
export interface GetStationAssignmentsResponse extends StationAssignmentPlan {
  lastModifiedBy: string;
  lastModifiedDate: string;
}

export interface StationAssignmentResponse extends  StationAssignmentPlan {
  lastModifiedBy: string;
  lastModifiedDate: string;
}

/**
 * Station assignment data structure used in the publish request payload.
 * Structurally identical to GetStationAssignmentsResponse but named to
 * reflect its role as the submission payload rather than a GET response.
 */
export interface StationAssignmentPlan {
  firstHalfStationAssignment: StationAssignmentHalfData;
  secondHalfStationAssignment: StationAssignmentHalfData;
}

/**
 * Empty half data used as the default/error fallback.
 */
const EMPTY_HALF: StationAssignmentHalfData = {
  associates: [],
  unassignedPALeadHRAssociates: [],
  processPaths: [],
  metrics: {
    totalUnlocked: 0,
    totalClockedIn: 0,
    totalActual: 0,
    assignedProcessPaths: [],
  },
};

/**
 * Empty response used as the default/error fallback in `api.ts`.
 */
export const EMPTY_RESPONSE: GetStationAssignmentsResponse = {
  firstHalfStationAssignment: EMPTY_HALF,
  secondHalfStationAssignment: EMPTY_HALF,
  lastModifiedBy: '',
  lastModifiedDate: '',
};

// ─── IsValidOverride API Types ──────────────────────────────────────────────────

/**
 * A single associate override entry for validation.
 */
export interface AssociateOverrideEntry {
  associateId: string;
  fclmFunctionId: string;
  fclmProcessPathId: number;
  targetWorkstationId: string;
}

/**
 * Request payload for the `/isValidStationAssignmentOverride` endpoint.
 * Contains 1 entry for a single move, 2 entries for a swap.
 */
export interface IsValidStationAssignmentOverrideRequest {
  nodeId: string;
  warehouseId: string;
  planId: string;
  overrides: AssociateOverrideEntry[];
}

/**
 * Response from the `/isValidStationAssignmentOverride` endpoint.
 */
export interface IsValidStationAssignmentOverrideResponse {
  validationErrors: string[];
}

/**
 * Request payload for the `/publishStationAssignment` endpoint.
 * Contains the plan metadata and the full assignment state (with all swaps applied).
 */
export interface PublishStationAssignmentRequest {
  planId: string;
  nodeId: string;
  warehouseId: string;
  ofdDate: string;
  clockWindowName: string;
  updatedBy: string;
  stationAssignment: StationAssignmentResponse;
}

// ─── SearchAssociateModal Types ─────────────────────────────────────────────────

/**
 * Context passed to SearchAssociateModal describing the target workstation
 * and its parent process path.
 */
export interface AssignModalTarget {
  workstationId: string;
  workstationName: string;
  fclmFunctionId: string;
  fclmProcessPathId: number | null;
}

export interface SearchAssociateModalProps {
  open: boolean;
  onClose: () => void;
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
  onSubmit: (associateId: string) => void;
  target: AssignModalTarget;
  /** When set, the modal operates in swap mode showing two pre-selected associates */
  swapAssociates?: [AssignmentAssociate, AssignmentAssociate];
}
